// Pure decision/formatting helpers for the lost-chargeback resolve panel (PROD-7808 T11).
// No I/O here — details.jsx passes data in and renders the results (unit-tested in
// resolve_helpers.test.js). Shapes follow CONTRACT-wave2.md.

import { has_super_user_access } from './helpers';

// Panel shows only for SUPER_USER on a lost dispute that isn't already closed out.
export const can_show_resolve_panel = (dispute, access_type) => {
  if (!dispute || !has_super_user_access(access_type)) return false;
  if (String(dispute.status || '').toLowerCase() !== 'lost') return false;
  const s = String(dispute.internal_status || '').toUpperCase();
  return s !== 'RESOLVED' && s !== 'NO_ACTION';
};

export const session_counts = (sessions) => {
  const s = sessions || {};
  return {
    one_on_one: Array.isArray(s.one_on_one) ? s.one_on_one.length : 0,
    gc_registrations: Array.isArray(s.gc_registrations) ? s.gc_registrations.length : 0,
    repeat_one_on_one: Array.isArray(s.repeat_one_on_one) ? s.repeat_one_on_one.length : 0,
    // G-C1: one-off GC bookings are now previewed + tokenized alongside o:/g:/r:.
    one_off_gc: Array.isArray(s.one_off_gc) ? s.one_off_gc.length : 0
  };
};

// repeat_slot_text: one weekly 1:1 repeat template line — repeat_time's weekday+time IS the slot;
// repeat_days is the per-week count ("Nx"). fmt_slot formats the datetime as weekday+time.
export const repeat_slot_text = (r, fmt_slot = (v) => v) => {
  const rr = r || {};
  const parts = [rr.repeat_time ? fmt_slot(rr.repeat_time) : 'unknown slot'];
  if (rr.duration) parts.push(`${rr.duration} min`);
  if (rr.repeat_days > 1) parts.push(`${rr.repeat_days}x/week`);
  return parts.join(' · ');
};

// credits_text: renewal branch reverses nothing (entitlement lives on the transaction).
export const credits_text = (credits) => {
  const c = credits || {};
  if (c.type_branch === 'renewal') {
    return 'no monetary credits to reverse (renewal — entitlement on transaction)';
  }
  const parts = [
    `granted ${c.granted ?? 0}`,
    `remaining ${c.remaining ?? 0}`,
    `reversible ${c.reversible ?? 0}`
  ];
  if (c.shortfall) parts.push(`shortfall ${c.shortfall}`);
  return `reverse ${c.reversible ?? 0} credit(s) (${parts.join(', ')})`;
};

// payments_warning_text: the panel's loudest warning — customer kept paying after disputing.
export const payments_warning_text = (payments, fmt_dt = (v) => v) => {
  const p = payments || {};
  if (!p.count) return null;
  const latest = p.latest_date ? ` (latest ${fmt_dt(p.latest_date)})` : '';
  return `Customer has PAID ${p.count} time${p.count === 1 ? '' : 's'} since this dispute${latest} — consider retaining instead`;
};

// entitlement_warning_text: B4 — never hide remaining entitlement on an already-cancelled membership.
export const entitlement_warning_text = (membership, fmt_dt = (v) => v) => {
  const m = membership || {};
  if (!m.already_cancelled || !m.entitled_until) return null;
  return `Membership already cancelled — entitled until ${fmt_dt(m.entitled_until)}`;
};

// loose_active_warning_text: H-M4/D1 — a live recurring row (past next_billing_date or
// PAUSED_PAYMENT_FAILURE) still exists; apis cancel WILL be attempted, never "nothing to revoke".
export const loose_active_warning_text = (membership) => {
  const m = membership || {};
  if (!m.loose_active_row) return null;
  return 'Membership row is still live (e.g. past next billing date or PAUSED_PAYMENT_FAILURE) — cancellation via apis WILL be attempted; verify the outcome after executing';
};

// contestable_dispute: evidence can only be submitted to Stripe while the dispute is open for
// response and its deadline hasn't passed — the UI mirror of the server's submitEvidenceGate (B8).
// Once a dispute is lost/won/closed (or past deadline), the whole Evidence block is dead UI, so
// details.jsx hides it. `now` is injected for testability.
export const contestable_dispute = (dispute, now = new Date()) => {
  const d = dispute || {};
  const s = String(d.status || '').toLowerCase();
  if (s !== 'needs_response' && s !== 'warning_needs_response') return false;
  if (d.evidence_due_by) {
    const due = new Date(d.evidence_due_by);
    if (!Number.isNaN(due.getTime()) && due.getTime() <= now.getTime()) return false;
  }
  return true;
};

// retain_recommended: the customer kept paying after disputing, so RETAIN is the recommended
// default and the destructive revoke must be guarded (PROD-7808 care-safety). Reads the same
// payments_since_dispute signal that drives the loud banner.
export const retain_recommended = (preview) => {
  const p = (preview && preview.payments_since_dispute) || {};
  return Boolean(p.count && p.count > 0);
};

// revoke_ack_text: the checkbox an operator must tick to UNLOCK the revoke when retain is
// recommended — forces an explicit "I know this customer paid and still want to cancel", so the
// destructive action can't be a reflex click.
export const revoke_ack_text = (preview, fmt_dt = (v) => v) => {
  const p = (preview && preview.payments_since_dispute) || {};
  if (!p.count) return ''; // only rendered when retain_recommended, but stay safe in isolation
  const latest = p.latest_date ? ` (latest ${fmt_dt(p.latest_date)})` : '';
  return `This customer has PAID ${p.count} time${
    p.count === 1 ? '' : 's'
  } since the dispute${latest}. I have reviewed this and still want to cancel their membership + sessions.`;
};

// revoke_confirm_warning: the loud last-chance line shown INSIDE the execute-confirm dialog when
// retain is recommended — the "are you sure?" the operator sees at the moment of committing.
// Returns null when the customer has not paid since (revoke is the expected remedy there).
export const revoke_confirm_warning = (preview, fmt_dt = (v) => v) => {
  const p = (preview && preview.payments_since_dispute) || {};
  if (!p.count) return null;
  const latest = p.latest_date ? ` (latest ${fmt_dt(p.latest_date)})` : '';
  return `Are you sure? This customer has PAID ${p.count} time${
    p.count === 1 ? '' : 's'
  } since the last chargeback${latest} — cancelling their membership + sessions is NOT recommended. Retain instead unless you have a specific reason.`;
};

// nothing_to_action: the lost dispute's remedy is already fully in effect — membership not live
// (already cancelled, no loose row), no sessions in ANY of the four sources, no reversible credits,
// and the customer is NOT a kept-paying retain candidate. In this state BOTH panel actions are
// mislabeled ("Apply resolve" applies nothing; "Close — no action (retain)" retains a cancelled
// account), so details.jsx collapses them to one neutral "mark resolved" close. (PROD-7808 care-UX.)
export const nothing_to_action = (preview) => {
  const p = preview || {};
  const m = p.membership || {};
  if (!m.already_cancelled || m.loose_active_row) return false; // membership still actionable
  const c = session_counts(p.sessions);
  if (c.one_on_one || c.gc_registrations || c.repeat_one_on_one || c.one_off_gc) return false;
  if (((p.credits || {}).reversible ?? 0) > 0) return false; // credits still reversible
  if (retain_recommended(p)) return false; // kept paying → that's the retain path, not a no-op
  return true;
};

// summarize_preview_for_confirm: the terse restatement shown on the inline confirm step.
export const summarize_preview_for_confirm = (preview, fmt_dt = (v) => v) => {
  const p = preview || {};
  const m = p.membership || {};
  const counts = session_counts(p.sessions);
  let membership;
  if (m.loose_active_row) {
    // H-M4: a live row exists despite the strict predicate — apis cancel WILL be attempted.
    membership = `Membership: live row found (loose match, ${m.subscription_status || 'unknown status'}) — cancellation via apis will be attempted`;
  } else if (m.already_cancelled) {
    membership = `Membership: already cancelled${m.entitled_until ? ` — entitled until ${fmt_dt(m.entitled_until)}` : ''}`;
  } else {
    membership = `Membership: cancel immediately (txn #${m.transaction_id ?? '?'}, ${m.subscription_status || 'unknown status'})`;
  }
  return [
    membership,
    `Sessions: cancel ${counts.one_on_one} upcoming 1:1 session(s) + ${counts.gc_registrations} GC registration(s) + ${counts.one_off_gc} one-off group class(es) + ${counts.repeat_one_on_one} weekly repeat slot(s)`,
    `Credits: ${credits_text(p.credits)}`
  ];
};

// steps_progress: non-empty only when a previous run left state behind (resume view).
export const steps_progress = (steps) => {
  const s = steps || {};
  const rows = ['membership', 'sessions', 'credits']
    .filter((k) => s[k])
    .map((k) => ({ step: k, state: s[k] }));
  return rows.some((r) => r.state !== 'PENDING') ? rows : [];
};

export const resolve_button_label = (steps) =>
  steps_progress(steps).length > 0 ? 'Resume resolve actions' : 'Apply resolve actions';

// classify_execute_response: bucket the HTTP-200 execute payload for rendering.
// 'error' = missing/empty envelope (nothing to render — don't mislabel as partial);
// 'aborted' = a concurrent run holds a step claim; 'resolved' = fully done; else 'partial'.
// H-M2: failures ALWAYS outrank finalized — a run with failed items must never show green.
export const classify_execute_response = (data) => {
  const d = data || {};
  const failures = Array.isArray(d.failures) ? d.failures : [];
  const has_signal =
    d.aborted_reason || d.finalized || d.internal_status || d.steps || d.results || failures.length;
  if (!has_signal) return { kind: 'error', reason: '', failures: [] };
  if (d.aborted_reason) return { kind: 'aborted', reason: d.aborted_reason, failures };
  if (failures.length > 0) return { kind: 'partial', reason: '', failures };
  if (d.finalized || String(d.internal_status || '').toUpperCase() === 'RESOLVED') {
    return { kind: 'resolved', reason: '', failures };
  }
  return { kind: 'partial', reason: '', failures };
};

// Per-step result lines (H-M1): render whenever the step reported AT ALL — a sessions step
// that failed before building items, or a credits step with only a note, must still show.
export const membership_result_text = (mem) => {
  if (!mem) return null;
  return `Membership: ${mem.outcome || 'no outcome reported'}${mem.note ? ` — ${mem.note}` : ''}`;
};

export const sessions_result_text = (ses) => {
  if (!ses) return null;
  const counts = `Sessions: ${ses.cancelled || 0} cancelled · ${ses.already_cancelled || 0} already cancelled · ${ses.failed || 0} failed`;
  return ses.note ? `${counts} — ${ses.note}` : counts;
};

export const credits_result_text = (cred) => {
  if (!cred) return null;
  if (!cred.branch) return `Credits: ${cred.note || 'no result reported'}`;
  return `Credits (${cred.branch}): granted ${cred.granted ?? 0}, reversed ${cred.reversed ?? 0}, shortfall ${cred.shortfall ?? 0}${cred.note ? ` — ${cred.note}` : ''}`;
};

// flatten_session_items: results.sessions.items keys are r:<uuid> | o:<uuid> | g:<id> | s:<uuid>.
export const flatten_session_items = (items) =>
  Object.entries(items || {}).map(([key, v]) => {
    let kind = 'GC (one-off)';
    if (key.startsWith('r:')) kind = 'Repeat 1:1';
    else if (key.startsWith('o:')) kind = '1:1';
    else if (key.startsWith('g:')) kind = 'GC (repeat)';
    return { key, kind, outcome: (v && v.outcome) || '', detail: (v && v.detail) || '' };
  });
